import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Checkbox } from "../components/ui/checkbox";
import { ExternalLink, Clock, Star, ArrowLeft, CalendarPlus, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { detectReminder, downloadICS, googleCalendarUrl } from "../lib/calendar";

export default function TaskDetail() {
  const { id } = useParams();
  const { user, refresh } = useAuth();
  const [task, setTask] = useState(null);
  const [busyStep, setBusyStep] = useState(null);
  const [calendarMenu, setCalendarMenu] = useState(null); // step index whose menu is open

  useEffect(() => {
    api.get(`/tasks/${id}`).then(({ data }) => setTask(data));
  }, [id]);

  const isDone = (user?.completed_tasks || []).includes(id);
  const doneSteps = useMemo(() => {
    const map = user?.task_steps_progress || {};
    return new Set(map[id] || []);
  }, [user, id]);

  if (!task) return <div className="text-white/60">Loading...</div>;

  const totalSteps = task.steps?.length || 0;
  const completedCount = isDone ? totalSteps : doneSteps.size;
  const progressPct = totalSteps === 0 ? 0 : Math.round((completedCount / totalSteps) * 100);

  const toggleStep = async (stepIdx, nextDone) => {
    if (isDone || busyStep !== null) return; // locked after full completion
    setBusyStep(stepIdx);
    try {
      const { data } = await api.post("/tasks/step-toggle", {
        task_id: task.task_id,
        step_index: stepIdx,
        done: nextDone,
      });
      if (data.auto_completed) {
        toast.success(`Task complete! +${data.points_earned} XP earned`);
        if (data.leveled_up) {
          setTimeout(() => toast.success(`🎉 Level up to ${data.user.level}`), 800);
        }
      }
      await refresh();
    } catch {
      toast.error("Couldn't update step");
    } finally {
      setBusyStep(null);
    }
  };

  const undoComplete = async () => {
    try {
      await api.post("/tasks/uncomplete", { task_id: task.task_id });
      toast("Marked incomplete");
      await refresh();
    } catch { toast.error("Couldn't update"); }
  };

  const addCalendar = (stepIdx, kind) => {
    const stepText = task.steps[stepIdx];
    const reminder = detectReminder(stepText);
    if (!reminder) {
      toast.error("Couldn't detect a date in this step");
      return;
    }
    const payload = {
      title: `S0rted reminder: ${stepText}`.slice(0, 200),
      description: `From task "${task.title}" on S0rted.\n\nStep: ${stepText}`,
      whenISO: reminder.whenISO,
      recurrence: reminder.recurrence,
      interval: reminder.interval || 1,
    };
    if (kind === "google") {
      window.open(googleCalendarUrl(payload), "_blank", "noopener");
    } else {
      downloadICS(payload);
    }
    setCalendarMenu(null);
    toast.success(`Reminder added (${reminder.label})`);
  };

  return (
    <div data-testid="task-detail-page" className="max-w-3xl">
      <Link to={`/categories/${task.category_id}`} className="text-xs text-white/50 hover:text-white flex items-center gap-1">
        <ArrowLeft size={12}/> Back to category
      </Link>

      <div className="glass-card p-7 md:p-9 mt-3">
        <div className="flex flex-wrap items-center gap-3 text-xs">
          <span className="px-3 py-1 rounded-full bg-neonyellow/15 text-neonyellow font-bold flex items-center gap-1"><Star size={12}/> +{task.points} XP</span>
          <span className="px-3 py-1 rounded-full bg-white/8 text-white/70 capitalize">{task.difficulty}</span>
          <span className="px-3 py-1 rounded-full bg-white/8 text-white/70 flex items-center gap-1"><Clock size={12}/> {task.estimated_time}</span>
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight mt-4">{task.title}</h1>
        <p className="text-white/70 mt-3 leading-relaxed">{task.why}</p>

        {/* Progress bar */}
        <div className="mt-7 flex items-center gap-3" data-testid="task-progress">
          <div className="flex-1 h-2 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-neoncyan to-neongreen transition-all duration-300"
                 style={{ width: `${progressPct}%` }}/>
          </div>
          <span className="text-xs text-white/60 font-bold whitespace-nowrap">
            {completedCount} / {totalSteps} {completedCount === totalSteps ? "done ✓" : "steps"}
          </span>
        </div>

        <h2 className="font-display text-xl font-extrabold mt-7">Step by step</h2>
        <ol className="mt-3 space-y-2.5">
          {task.steps.map((s, i) => {
            const checked = isDone || doneSteps.has(i);
            const reminder = detectReminder(s);
            const menuOpen = calendarMenu === i;
            return (
              <li key={`step-${i}`}
                  data-testid={`task-step-${i}`}
                  className={`group flex items-start gap-3 p-3 rounded-2xl border transition ${
                    checked ? "bg-neongreen/5 border-neongreen/25" : "bg-white/5 border-white/10 hover:bg-white/8"
                  }`}>
                <Checkbox
                  checked={checked}
                  disabled={isDone || busyStep !== null}
                  onCheckedChange={(v) => toggleStep(i, !!v)}
                  data-testid={`task-step-check-${i}`}
                  className="size-6 rounded-full mt-0.5 flex-shrink-0 data-[state=checked]:bg-neongreen data-[state=checked]:border-neongreen border-neoncyan/60"
                />
                <div className="flex-1 min-w-0">
                  <div className={`leading-relaxed transition ${checked ? "text-white/45 line-through" : "text-white/85"}`}>
                    <span className="text-xs text-neoncyan font-bold mr-2">{i + 1}.</span>{s}
                  </div>
                  {reminder && !checked && (
                    <div className="relative mt-2">
                      <button
                        type="button"
                        onClick={() => setCalendarMenu(menuOpen ? null : i)}
                        data-testid={`task-step-calendar-${i}`}
                        className="inline-flex items-center gap-1.5 text-xs text-neoncyan hover:text-neonyellow transition px-2 py-1 rounded-full bg-neoncyan/10 hover:bg-neonyellow/10"
                      >
                        <CalendarPlus size={12}/> Add reminder · {reminder.label}
                      </button>
                      {menuOpen && (
                        <div className="absolute left-0 mt-1.5 z-10 bg-surface border border-white/15 rounded-2xl p-2 shadow-2xl text-xs flex flex-col min-w-[180px]"
                             data-testid={`task-step-calendar-menu-${i}`}>
                          <button onClick={() => addCalendar(i, "google")}
                                  data-testid={`task-step-calendar-google-${i}`}
                                  className="text-left px-3 py-2 hover:bg-white/10 rounded-xl">📅 Google Calendar</button>
                          <button onClick={() => addCalendar(i, "ics")}
                                  data-testid={`task-step-calendar-ics-${i}`}
                                  className="text-left px-3 py-2 hover:bg-white/10 rounded-xl">📥 Apple / Outlook (.ics)</button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>

        {task.links?.length > 0 && (
          <>
            <h2 className="font-display text-xl font-extrabold mt-8">Trusted links</h2>
            <div className="space-y-2 mt-3">
              {task.links.map((l) => (
                <a key={l.url} href={l.url} target="_blank" rel="noopener noreferrer"
                  data-testid={`task-link-${l.url}`}
                  className="flex items-center justify-between gap-3 px-4 py-3 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 transition">
                  <span className="font-semibold text-sm">{l.label}</span>
                  <ExternalLink size={14} className="text-white/50"/>
                </a>
              ))}
            </div>
          </>
        )}

        <div className="flex items-center gap-4 mt-9 p-5 rounded-2xl bg-white/5 border border-white/10">
          <div className={`size-10 rounded-full flex items-center justify-center ${isDone ? "bg-neongreen/20 text-neongreen" : "bg-white/10 text-white/40"}`}>
            <CheckCircle2 size={20}/>
          </div>
          <div className="flex-1">
            <div className="font-display font-bold">
              {isDone ? "Completed!" :
                completedCount === totalSteps ? "All steps ticked — saving…" :
                `${totalSteps - completedCount} step${totalSteps - completedCount === 1 ? "" : "s"} to go`}
            </div>
            <div className="text-xs text-white/55">
              {isDone ? "Nice work. You earned XP for this." :
                `Tick every step to earn +${task.points} XP automatically.`}
            </div>
          </div>
          {isDone && (
            <button onClick={undoComplete} data-testid="task-detail-toggle-button" className="btn-secondary-neon text-sm">
              Mark incomplete
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
